import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Tooltip } from '@mantine/core';
import { Icon } from '@shakkerai/components';
import { upscaleSettingScalesList } from '@shakkerai/pro-constants';
import { NumberInputGroup, GenerateButton, ButtonGroup } from '@shakkerai/pro-components';
import { GeneratorPanelFooter, SizeInputGroup } from '@starflow/app/modules/generator/components/_legacy';
import { updateUnitData } from '@starflow/app/helpers/flow';
import { scheduleTaskForUnit } from '@starflow/app/helpers/process';
import { translate } from '@shakkerai/util-i18n';

const Preparing = () => {
  return <div />;
};

const LENGTH_LIMIT = 8192;

export const upscaleOptions = [
  { value: '1', label: translate('upscale_tile_moredetail') },
  { value: '2', label: translate('upscale_tile_hdr') },
];

export const UpscalePanel = (props) => {
  const { element, generator } = props;

  const [scale, setScale] = useState(1);

  const handleUpscale = useCallback(() => {
    // debugger;
    scheduleTaskForUnit(generator.id);
  }, [generator.id]);

  const handleChange = useCallback(
    (value, name) => {
      updateUnitData(generator.id, { [name]: value });
    },
    [generator.id],
  );

  useEffect(() => {
    if (generator.data?.scaleFactor) {
      const slr = generator.data.scaleFactor;
      setScale(slr);
      handleChangeScale(generator.data.scaleFactor);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generator.data?.scaleFactor]);

  /**
   * 原始尺寸
   */
  const imageSize = useMemo(() => {
    return {
      width: element.width,
      height: element.height,
    };
  }, [element]);

  /**
   * 是否可放大
   */
  const controlEnable = useMemo(() => {
    return Math.min(imageSize.width, imageSize.height) >= 256;
  }, [imageSize]);

  const maxRate = useMemo(() => {
    const maxValue = Math.max(imageSize.width, imageSize.height);
    const maxScale = Math.floor((LENGTH_LIMIT / maxValue) * 10) / 10;
    return maxScale;
  }, [imageSize]);

  /**
   * 放大尺寸
   */
  const scaleSize = useMemo(() => {
    return {
      width: Math.round(generator.data.width * scale),
      height: Math.round(generator.data.height * scale),
    };
  }, [imageSize, scale]);

  /**
   * 滑动结束
   */
  const handleChangeScale = useCallback((scale) => {
    setScale(scale);
  }, []);

  const handleSelected = (item) => {
    handleChange(item.value, 'scaleFactor');
  };

  if (!generator) {
    return (
      <div id="upscaper-generator-panel" className="px-4 flex-1 overflow-auto tiny-scrollbar">
        <Preparing preparing={true} imageId={element.id} />
      </div>
    );
  }
  return (
    <>
      <h2 className="flex items-center px-4 h-[44px] border-b border-[#efefef] font-bold text-sm text-[#2d2d2d] font-grotesk">
        {translate('imagetool_upscale')}
      </h2>
      <div id="upscaper-generator-panel" className="px-4 flex-1 overflow-auto tiny-scrollbar">
        <div className="items-center mt-4">
          <div className="text-xs text-[#2d2d2d] font-semibold">{translate('imagetool_upscale_scale')}</div>
          <div className="flex justify-between mt-3 space-x-3">
            {upscaleSettingScalesList.map((item) => {
              const disabled = item.value > maxRate;
              const Item = (
                <button
                  className={`h-6 border rounded-sm text-xs w-[46px] ${
                    generator.data.scaleFactor === item.value
                      ? 'bg-primary/10 border-primary'
                      : 'border-transparent bg-[#f4f6f8] text-[#2d2d2d]'
                  }`}
                  key={item.label}
                  onClick={() => !disabled && handleSelected(item)}
                >
                  {item.label}
                </button>
              );
              if (disabled) {
                return (
                  <Tooltip
                    key={item.label}
                    withArrow
                    className="translate-y-[5px] scale-90"
                    label={translate('upscale_max_tips', { value: 8192 })}
                  >
                    {Item}
                  </Tooltip>
                );
              }
              return Item;
            })}
          </div>
          <div className="flex items-center justify-between mt-3">
            <span className="text-xs text-[#8c8c8c]">
              {Math.round(generator.data.width)}*{Math.round(generator.data.height)}
            </span>
            <Icon className="icon-pro_arrow_right mx-1" size={12} />
            <SizeInputGroup disabled={true} lockRatio value={scaleSize} />
          </div>
        </div>
        <div className="-mx-4 my-3.5 border-t border-[#efefef]" />
        <div className="text-[14px] text-[#222] font-semibold">{translate('imagetool_upscale_params')}</div>
        <div className="flex mt-3 items-center h-6">
          <span className="text-xs text-[#7f7f7f] block w-[98px] flex-shrink-0">{translate('upscale_tile_type')}</span>
          <ButtonGroup
            classNames={'flex justify-between w-full'}
            options={upscaleOptions}
            selectedId={generator.data.upscaleType}
            onChange={(value) => handleChange(value, 'upscaleType')}
          />
        </div>
        {generator.data.upscaleType === '1' ? (
          <div className="flex mt-3 items-center h-6">
            <div className="flex items-center w-[98px]">
              <span className="text-xs text-[#7f7f7f]">{translate('upscale_tile_weight')}</span>
            </div>
            <NumberInputGroup
              min={0}
              max={0.8}
              step={0.1}
              name="detailFactor"
              inputWidth={40}
              sliderLabel={null}
              value={generator.data.detailFactor}
              onChange={(value) => handleChange(value, 'detailFactor')}
            />
          </div>
        ) : null}
      </div>
      <GeneratorPanelFooter generator={generator}>
        <GenerateButton disabled={!controlEnable} onClick={handleUpscale}>
          {translate('imagetool_upscale')}
        </GenerateButton>
      </GeneratorPanelFooter>
    </>
  );
};
